import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { LOGO } from "../mock";
import { track } from "../lib/analytics";

const NAV = [
  { label: "The Menopause Matrix\u2122", href: "/matrix" },
  { label: "How It Works", href: "/#how", section: "how" },
  { label: "About", href: "/#about", section: "about" },
  { label: "Services", children: [
    { label: "Full Stack Coaching", href: "/services/full-stack-coaching" },
    { label: "Lifestyle & Longevity Coaching", href: "/services/lifestyle-coaching" },
    { label: "Competition Coaching", href: "/services/competition-coaching" },
    { label: "Rx Club", href: "/services/rx-club" },
    { label: "12-Week Transformation", href: "/services/12-week-transformation" },
  ] },
  {
    label: "Free",
    children: [
      { label: "6-Week Transformation", href: "https://start.sassyfitness.com", external: true },
      { label: "Future You\u2122", href: "https://futureyou.sassyfitness.com", external: true },
    ],
  },
  { label: "Stores", href: "/stores" },
  { label: "Blog", href: "https://blog.sassyfitness.com" },
];

const Header = ({ minimal = false }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [openSub, setOpenSub] = useState({});
  const toggleSub = (label) => setOpenSub((s) => ({ ...s, [label]: !s[label] }));
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goMatrix = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setOpen(false);
    track("take_matrix_click", { location: "header" });
    if (location.pathname === "/matrix") {
      const el = document.getElementById("matrix");
      if (el) el.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/matrix");
    }
  };

  // Section links (How It Works, About): scroll on home, otherwise route home then scroll.
  const goToSection = (id) => (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setOpen(false);
    if (location.pathname === "/") {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/", { state: { scrollTo: id } });
    }
  };

  const Logo = (
    <a href="/" className="flex items-center focus-ring rounded-sm">
      <img
        src={LOGO}
        alt="Sassy Full Stack Fitness"
        className="h-11 md:h-14 w-auto object-contain"
      />
    </a>
  );

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-background/85 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="w-[90%] max-w-[1400px] mx-auto flex items-center justify-between h-[72px]">
        {Logo}

        {!minimal && (
          <>
            <nav className="hidden md:flex items-center gap-7 lg:gap-9">
              {NAV.map((n) =>
                n.children ? (
                  <div key={n.label} className="relative group" data-testid={`nav-${n.label.toLowerCase()}-dropdown`}>
                    <button
                      className="flex items-center gap-1 text-[13px] font-semibold tracking-[0.15em] uppercase text-white/70 group-hover:text-white transition-colors focus-ring rounded-sm whitespace-nowrap"
                      data-testid={`nav-${n.label.toLowerCase()}-trigger`}
                    >
                      {n.label}
                      <ChevronDown size={13} className="transition-transform duration-200 group-hover:rotate-180" />
                    </button>
                    <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 group-focus-within:opacity-100 group-focus-within:visible transition-all duration-150 z-50">
                      <div className="min-w-[240px] rounded-xl bg-background/95 backdrop-blur-xl border border-white/10 shadow-[0_20px_50px_-12px_rgba(0,0,0,0.8)] py-2">
                        {n.children.map((c) => (
                          <a
                            key={c.label}
                            href={c.href}
                            target={c.external ? "_blank" : undefined}
                            rel={c.external ? "noopener noreferrer" : undefined}
                            onClick={c.external ? () => track("outbound_click", { url: c.href, label: c.label }) : undefined}
                            data-testid={`nav-${n.label.toLowerCase()}-item-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                            className="block px-5 py-3 text-[12px] font-semibold tracking-[0.12em] uppercase text-white/75 hover:text-white hover:bg-white/5 transition-colors whitespace-nowrap"
                          >
                            {c.label}
                          </a>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <a
                    key={n.label}
                    href={n.href}
                    onClick={
                      n.section
                        ? goToSection(n.section)
                        : n.href === "/matrix"
                        ? goMatrix
                        : n.href && n.href.startsWith("http")
                        ? () => track("outbound_click", { url: n.href, label: n.label })
                        : undefined
                    }
                    target={n.external ? "_blank" : undefined}
                    rel={n.external ? "noopener noreferrer" : undefined}
                    className="text-[13px] font-semibold tracking-[0.15em] uppercase text-white/70 hover:text-white transition-colors focus-ring rounded-sm whitespace-nowrap"
                  >
                    {n.label}
                  </a>
                )
              )}
            </nav>

            <div className="flex items-center gap-3">
              <button
                onClick={goMatrix}
                className="hidden sm:inline-flex items-center rounded-full bg-primary text-primary-foreground font-bold text-[13px] tracking-wide px-6 py-2.5 hover:brightness-110 active:scale-[0.98] transition focus-ring shadow-[0_6px_24px_-6px_rgba(212,175,55,0.6)]"
              >
                Take the Matrix
              </button>
              <button
                aria-label="Open menu"
                className="md:hidden text-white p-2 focus-ring rounded-md"
                onClick={() => setOpen((o) => !o)}
              >
                {open ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </>
        )}
      </div>

      {/* Mobile menu */}
      {!minimal && open && (
        <div className="md:hidden bg-background/95 backdrop-blur-xl border-b border-white/10 px-[5%] py-6 space-y-4">
          {NAV.map((n) =>
            n.children ? (
              <div key={n.label} className="space-y-3" data-testid={`nav-${n.label.toLowerCase()}-mobile`}>
                <button
                  onClick={() => toggleSub(n.label)}
                  className="w-full flex items-center justify-between text-sm font-semibold tracking-[0.15em] uppercase text-white/80 hover:text-white"
                  data-testid={`nav-${n.label.toLowerCase()}-mobile-toggle`}
                  aria-expanded={!!openSub[n.label]}
                >
                  <span>{n.label}</span>
                  <ChevronDown
                    size={18}
                    className={`transition-transform duration-200 ${openSub[n.label] ? "rotate-180" : ""}`}
                  />
                </button>
                {openSub[n.label] && (
                  <div className="pl-4 space-y-3 border-l border-white/10">
                    {n.children.map((c) => (
                      <a
                        key={c.label}
                        href={c.href}
                        target={c.external ? "_blank" : undefined}
                        rel={c.external ? "noopener noreferrer" : undefined}
                        onClick={() => {
                          if (c.external) track("outbound_click", { url: c.href, label: c.label });
                          setOpen(false);
                        }}
                        className="block text-[13px] font-semibold tracking-[0.12em] uppercase text-white/65 hover:text-white"
                      >
                        {c.label}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <a
                key={n.label}
                href={n.href}
                onClick={(e) => {
                  if (n.section) goToSection(n.section)(e);
                  else if (n.href === "/matrix") goMatrix(e);
                  else {
                    if (n.href && n.href.startsWith("http"))
                      track("outbound_click", { url: n.href, label: n.label });
                    setOpen(false);
                  }
                }}
                target={n.external ? "_blank" : undefined}
                rel={n.external ? "noopener noreferrer" : undefined}
                className="block text-sm font-semibold tracking-[0.15em] uppercase text-white/80 hover:text-white"
              >
                {n.label}
              </a>
            )
          )}
          <button
            onClick={goMatrix}
            className="w-full rounded-full bg-primary text-primary-foreground font-bold text-sm px-6 py-3"
          >
            Take the Matrix
          </button>
        </div>
      )}
    </header>
  );
};

export default Header;
